import {useEffect, useRef} from 'react';
import {createPhotonRenderer} from './photon-renderer';
import './photon-field.css';

/** Selected Hero B: decorative light particles with a static fallback. */
export function PhotonField() {
  const layerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const layer = layerRef.current;
    const canvas = canvasRef.current;
    const section = layer?.closest<HTMLElement>('.research-journey');
    const intro = section?.querySelector<HTMLElement>('.journey-intro');
    if (!layer || !canvas || !section || !intro) return;

    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const contrast = matchMedia('(forced-colors: active)');
    const compact = matchMedia('(max-width: 680px)');
    let renderer: ReturnType<typeof createPhotonRenderer> = null;
    let raf = 0, last = 0, elapsed = 0, visible = true, disposed = false;
    let x = .5, y = .5, targetX = .5, targetY = .5;

    const stop = () => { cancelAnimationFrame(raf); raf = 0; last = 0; };
    const draw = () => {
      renderer?.draw(reduced.matches ? 6 : 6 + elapsed, x, y);
    };
    const loop = (now: number) => {
      raf = 0;
      if (disposed || !renderer || !visible || document.hidden || reduced.matches || contrast.matches) return;
      const dt = last ? Math.min((now - last) / 1000, .05) : 0;
      last = now;
      elapsed += dt;
      const follow = 1 - Math.exp(-4.8 * dt);
      x += (targetX - x) * follow;
      y += (targetY - y) * follow;
      draw();
      raf = requestAnimationFrame(loop);
    };
    const resume = () => {
      if (!disposed && renderer && visible && !document.hidden && !reduced.matches && !contrast.matches && !raf) {
        last = 0;
        raf = requestAnimationFrame(loop);
      }
    };
    const resize = () => {
      // Anchor the light field to the intro, not the much taller pinned research section.
      const height = intro.getBoundingClientRect().bottom - section.getBoundingClientRect().top + (compact.matches ? 155 : 230);
      layer.style.height = `${Math.ceil(height)}px`;
      const width = layer.clientWidth;
      const dpr = Math.min(devicePixelRatio || 1, compact.matches ? 1.5 : 1.35);
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      renderer?.resize(dpr * (compact.matches ? .7 : 1));
      draw();
      resume();
    };
    const initialize = () => {
      stop();
      renderer?.dispose();
      renderer = null;
      layer.dataset.renderState = 'fallback';
      if (contrast.matches || disposed) return;
      renderer = createPhotonRenderer(canvas, compact.matches ? 24000 : 68000);
      if (renderer) {
        resize();
        layer.dataset.renderState = 'ready';
      }
    };
    const move = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || reduced.matches) return;
      const rect = canvas.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientY > rect.bottom) return;
      targetX = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      targetY = 1 - Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
    };
    const leave = () => { targetX = targetY = .5; };
    const preferenceChanged = () => {
      if (reduced.matches) {
        stop();
        x = y = targetX = targetY = .5;
        elapsed = 0;
        draw();
      } else resume();
    };
    const visibilityChanged = () => { if (document.hidden) stop(); else resume(); };
    const contextLost = (event: Event) => {
      event.preventDefault();
      stop();
      renderer?.dispose();
      renderer = null;
      layer.dataset.renderState = 'fallback';
    };

    const observer = new ResizeObserver(resize);
    observer.observe(intro);
    const intersection = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) resume(); else stop();
    });
    intersection.observe(layer);
    section.addEventListener('pointermove', move, {passive: true});
    section.addEventListener('pointerleave', leave);
    window.addEventListener('resize', resize, {passive: true});
    document.addEventListener('visibilitychange', visibilityChanged);
    reduced.addEventListener('change', preferenceChanged);
    compact.addEventListener('change', initialize);
    contrast.addEventListener('change', initialize);
    canvas.addEventListener('webglcontextlost', contextLost);
    canvas.addEventListener('webglcontextrestored', initialize);
    initialize();

    return () => {
      disposed = true;
      stop();
      observer.disconnect();
      intersection.disconnect();
      section.removeEventListener('pointermove', move);
      section.removeEventListener('pointerleave', leave);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', visibilityChanged);
      reduced.removeEventListener('change', preferenceChanged);
      compact.removeEventListener('change', initialize);
      contrast.removeEventListener('change', initialize);
      canvas.removeEventListener('webglcontextlost', contextLost);
      canvas.removeEventListener('webglcontextrestored', initialize);
      renderer?.dispose();
    };
  }, []);

  return <div ref={layerRef} className="photon-field" data-render-state="fallback" aria-hidden="true">
    <canvas ref={canvasRef}/>
  </div>;
}
